import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

function AdminUserDetail() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genMessage, setGenMessage] = useState(null);
  const [polling, setPolling] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/.netlify/functions/admin-get-users', {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      const found = data.users.find(u => u.id === userId);
      if (found) {
        setUser(found);
      } else {
        setError('User not found');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Poll for PASS report completion when generating
  useEffect(() => {
    if (!polling || !userId) return;

    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/.netlify/functions/admin-get-users', {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const found = data.users.find(u => u.id === userId);
        if (found && found.report_status === 'completed') {
          setUser(found);
          setPolling(false);
          setGenerating(false);
          setGenMessage('Report generated successfully!');
        }
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [polling, userId]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    setGenMessage(null);

    try {
      const response = await fetch('/.netlify/functions/generate-report-v2-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      if (response.ok || response.status === 202) {
        setGenMessage('Report generation started. This usually takes 3-5 minutes...');
        setPolling(true);
      } else {
        const text = await response.text();
        setGenMessage(`Error: ${text}`);
        setGenerating(false);
      }
    } catch (err) {
      setGenMessage(`Error: ${err.message}`);
      setGenerating(false);
    }
  };

  if (loading) return <p style={styles.loading}>Loading user details...</p>;
  if (error) return <p style={styles.error}>Error: {error}</p>;
  if (!user) return <p style={styles.error}>User not found</p>;

  const hasReport = user.report_status === 'completed' && user.report_url;
  const products = user.products || {};
  const vbReports = products.voice_brand?.reports || [];
  const tdaReports = products.tda?.reports || [];

  return (
    <div>
      <Link to="/admin/users" style={styles.backLink}>← Back to Users</Link>

      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>{user.full_name || user.email}</h1>
          <p style={styles.subtitle}>{user.email}</p>
        </div>
        {user.is_admin && <span style={styles.adminTag}>Admin</span>}
      </div>

      {/* Quick info grid */}
      <div style={styles.infoGrid}>
        <InfoCard label="Stage" value={user.stage || '—'} />
        <InfoCard label="Profile Title" value={user.profile_title || '—'} />
        <InfoCard
          label="Created"
          value={user.created_at
            ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
            : '—'}
        />
        <InfoCard label="Payment" value={user.payment_status || '—'} />
      </div>

      {/* ============ PASS PROFILE ============ */}
      <ProductSection
        title="PASS Profile"
        accent="#1B5E20"
        owned={products.pass?.owned}
      >
        <div style={styles.productRow}>
          <span style={styles.productLabel}>Assessment</span>
          <StatusBadge status={products.pass?.assessment_status} />
        </div>
        <div style={styles.productRow}>
          <span style={styles.productLabel}>Report</span>
          <StatusBadge status={products.pass?.report_status} />
        </div>
        {user.report_generated_at && (
          <div style={styles.productRow}>
            <span style={styles.productLabel}>Generated</span>
            <span style={styles.productValue}>
              {new Date(user.report_generated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        )}

        <div style={styles.actionsRow}>
          <button
            onClick={handleGenerateReport}
            disabled={generating}
            style={{
              ...styles.actionBtn,
              ...(generating ? styles.actionBtnDisabled : {}),
            }}
          >
            {generating ? 'Generating...' : hasReport ? 'Re-generate Report' : 'Generate Report'}
          </button>
          {hasReport && (
            <a
              href={user.report_url}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.actionBtnLink}
            >
              Open PASS PDF
            </a>
          )}
        </div>

        {genMessage && (
          <div style={{
            ...styles.genMessage,
            ...(genMessage.startsWith('Error') ? styles.genMessageError : {}),
            ...(genMessage.includes('successfully') ? styles.genMessageSuccess : {}),
          }}>
            {polling && <span style={styles.miniSpinner}></span>}
            {genMessage}
          </div>
        )}
      </ProductSection>

      {/* ============ VOICE-BRAND ============ */}
      <ProductSection
        title="Voice-Brand"
        accent="#0D47A1"
        owned={products.voice_brand?.owned}
      >
        {!products.voice_brand?.owned ? (
          <p style={styles.notOwned}>Not purchased.</p>
        ) : vbReports.length === 0 ? (
          <p style={styles.notOwned}>Purchased, but no Voice-Brand input has been submitted yet.</p>
        ) : (
          <table style={styles.miniTable}>
            <thead>
              <tr>
                <th style={styles.miniTh}>Brand</th>
                <th style={styles.miniTh}>Status</th>
                <th style={styles.miniTh}>Generated</th>
                <th style={styles.miniTh}>Report</th>
              </tr>
            </thead>
            <tbody>
              {vbReports.map((r, i) => (
                <tr key={r.id || i} style={styles.miniRow}>
                  <td style={styles.miniTd}>{r.label || <span style={styles.muted}>—</span>}</td>
                  <td style={styles.miniTd}><StatusBadge status={r.status} /></td>
                  <td style={styles.miniTd}>
                    {r.generated_at
                      ? new Date(r.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </td>
                  <td style={styles.miniTd}>
                    {r.report_url ? (
                      <a href={r.report_url} target="_blank" rel="noopener noreferrer" style={styles.smallLink}>
                        Open PDF
                      </a>
                    ) : <span style={styles.muted}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ProductSection>

      {/* ============ TDA ============ */}
      <ProductSection
        title="TDA (Team Dynamics)"
        accent="#AD1457"
        owned={products.tda?.owned}
      >
        {!products.tda?.owned ? (
          <p style={styles.notOwned}>Not purchased.</p>
        ) : tdaReports.length === 0 ? (
          <p style={styles.notOwned}>Purchased, but no team has been assembled yet.</p>
        ) : (
          <table style={styles.miniTable}>
            <thead>
              <tr>
                <th style={styles.miniTh}>Team</th>
                <th style={styles.miniTh}>Status</th>
                <th style={styles.miniTh}>Generated</th>
                <th style={styles.miniTh}>Report</th>
              </tr>
            </thead>
            <tbody>
              {tdaReports.map((r, i) => (
                <tr key={r.id || i} style={styles.miniRow}>
                  <td style={styles.miniTd}>{r.label || <span style={styles.muted}>—</span>}</td>
                  <td style={styles.miniTd}><StatusBadge status={r.status} /></td>
                  <td style={styles.miniTd}>
                    {r.generated_at
                      ? new Date(r.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </td>
                  <td style={styles.miniTd}>
                    {r.report_url ? (
                      <a href={r.report_url} target="_blank" rel="noopener noreferrer" style={styles.smallLink}>
                        Open PDF
                      </a>
                    ) : <span style={styles.muted}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ProductSection>
    </div>
  );
}

function ProductSection({ title, accent, owned, children }) {
  return (
    <div style={{ ...styles.productCard, borderLeftColor: accent }}>
      <div style={styles.productHeader}>
        <h3 style={{ ...styles.productTitle, color: accent }}>{title}</h3>
        <span style={{
          ...styles.ownedBadge,
          backgroundColor: owned ? '#E8F5E9' : '#F5F5F5',
          color: owned ? '#1B5E20' : '#9E9E9E',
        }}>
          {owned ? 'Purchased' : 'Not purchased'}
        </span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div style={styles.infoCard}>
      <p style={styles.infoLabel}>{label}</p>
      <p style={styles.infoValue}>{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    completed: { bg: '#E8F5E9', color: '#2E7D32', label: 'Completed' },
    generating: { bg: '#FFF3E0', color: '#E65100', label: 'Generating' },
    in_progress: { bg: '#FFF3E0', color: '#E65100', label: 'In Progress' },
    pending: { bg: '#FFF8E1', color: '#F57F17', label: 'Pending' },
    input_pending: { bg: '#FFF8E1', color: '#F57F17', label: 'Input Pending' },
    not_started: { bg: '#F5F5F5', color: '#757575', label: 'Not Started' },
    none: { bg: '#F5F5F5', color: '#757575', label: 'None' },
  };
  const c = config[status] || config.none;
  return (
    <span style={{ ...styles.statusBadge, backgroundColor: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

const styles = {
  loading: { color: '#6B7A8F', fontSize: 15 },
  error: { color: '#D32F2F', fontSize: 15 },
  backLink: {
    display: 'inline-block',
    color: '#2A9D8F',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 20,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7A8F',
    margin: '4px 0 0 0',
  },
  adminTag: {
    background: '#0B3D2E',
    color: 'white',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 28,
  },
  infoCard: {
    background: 'white',
    borderRadius: 8,
    padding: '16px 20px',
    border: '1px solid #E8ECF0',
  },
  infoLabel: {
    fontSize: 11,
    color: '#6B7A8F',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
    margin: '0 0 8px 0',
  },
  infoValue: {
    fontSize: 15,
    color: '#0B3D2E',
    fontWeight: 600,
    margin: 0,
  },
  productCard: {
    background: 'white',
    borderRadius: 10,
    padding: '20px 24px',
    border: '1px solid #E8ECF0',
    borderLeft: '5px solid #2A9D8F',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: 20,
  },
  productHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  productTitle: {
    fontSize: 17,
    fontWeight: 700,
    margin: 0,
  },
  ownedBadge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  productRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #F5F6F8',
  },
  productLabel: {
    fontSize: 14,
    color: '#6B7A8F',
    fontWeight: 500,
  },
  productValue: {
    fontSize: 14,
    color: '#333',
    fontWeight: 500,
  },
  actionsRow: {
    display: 'flex',
    gap: 10,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  actionBtn: {
    background: '#2A9D8F',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  actionBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  actionBtnLink: {
    display: 'inline-block',
    background: '#0B3D2E',
    color: 'white',
    padding: '10px 20px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  notOwned: {
    fontSize: 13,
    color: '#9E9E9E',
    margin: 0,
    fontStyle: 'italic',
  },
  miniTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    marginTop: 4,
  },
  miniTh: {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7A8F',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '1px solid #E8ECF0',
  },
  miniRow: { borderBottom: '1px solid #F5F6F8' },
  miniTd: { padding: '8px 10px' },
  smallLink: {
    display: 'inline-block',
    background: '#2A9D8F',
    color: 'white',
    padding: '4px 10px',
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
  },
  muted: { color: '#B0B8C4' },
  genMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderRadius: 6,
    fontSize: 13,
    margin: '16px 0 0 0',
    background: '#F0FAF4',
    color: '#0B3D2E',
    border: '1px solid #C8E6D0',
  },
  genMessageError: {
    background: '#FFF3F3',
    color: '#D32F2F',
    border: '1px solid #FFCDD2',
  },
  genMessageSuccess: {
    background: '#E8F5E9',
    color: '#2E7D32',
    border: '1px solid #A5D6A7',
  },
  miniSpinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid #C8E6D0',
    borderTop: '2px solid #2A9D8F',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    flexShrink: 0,
  },
};

export default AdminUserDetail;
